import { cva } from 'class-variance-authority'
import React, { FocusEvent, ForwardedRef, InputHTMLAttributes, forwardRef, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { v4 as uuidv4 } from 'uuid'

import { cn } from '@frame/lib/utils/cn'
import { formatCurrency, formatDateWithSlash } from '@frame/lib/utils/format'
import { formatPhoneNumber } from '@frame/lib/utils/format-phone-number'
import { Transition } from '@headlessui/react'

import { Span } from '../span'
import { Label } from './label'
import { OptionalTag, RecommendedTag, RequiredTag } from './tags'

const INPUT_ID = 'base_input'

type InputType = 'dateString' | 'text' | 'currency' | 'email' | 'tel' | 'textArea' | 'password'
type TagType = 'required' | 'optional' | 'recommended'

const variants = cva(
  [
    'h-7',
    'w-full',
    'bg-white',
    'block',
    'border-moss-300',
    'border',
    'peer',
    'placeholder:opacity-0',
    'px-[20px]',
    'py-[14px]',
    'rounded-sm',
    'text-moss-700',
    'text-sm',
    'disabled:text-moss-500',
    'focus:border-1.5',
    'focus:border-electric-650',
    'focus:outline-none',
    'focus:placeholder:opacity-100',
    'focus:ring-0',
  ],
  {
    variants: {
      showError: {
        true: ['border-1.5', 'border-error-600', 'outline-none', 'placeholder:opacity-100'],
      },
      showWarning: {
        true: ['border-1.5', 'border-warning-600', 'outline-none', 'placeholder:opacity-100'],
      },
      type: {
        text: ['whitespace-nowrap', 'overflow-ellipsis', 'overflow-hidden'],
        currency: [],
        email: [],
        tel: [],
        textArea: 'min-h-[52px] resize-y',
        dateString: [],
        password: [],
        number: [],
      },
      applyCobranding: {
        true: ['focus:border-cobranding'],
      },
    },
    defaultVariants: {
      showError: false,
      showWarning: false,
      type: 'text',
      applyCobranding: true,
    },
  }
)

export type Props = {
  label: string
  type?: InputType
  value?: string | number
  tagType?: TagType | null
  error?: string
  warning?: string
  helperText?: string
  hasValueFromCombobox?: boolean
  applyCobranding?: boolean
} & InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>

export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
  (
    {
      label,
      type = 'text',
      tagType = null,
      error,
      warning,
      helperText,
      hasValueFromCombobox = false,
      required,
      disabled,
      value,
      applyCobranding = true,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const formattedValue = formatValue({ type, value })
    const labelTag = getLabelTag({ required, tagType })
    const showError = Boolean(error)
    const showWarning = Boolean(warning)
    const showHelperText = Boolean(helperText) && isFocused

    let blurTimeoutId: NodeJS.Timeout | null = null

    useEffect(() => {
      return () => {
        if (blurTimeoutId) {
          clearTimeout(blurTimeoutId)
        }
      }
    })

    function shouldShowLabelTag() {
      if (disabled) return false
      if (isFocused) return false
      if (showError) return false
      if (showWarning) return false
      if (formattedValue) return false
      if (hasValueFromCombobox) return false

      return true
    }

    function handleBlur(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
      props?.onBlur?.(e)

      if (blurTimeoutId) {
        clearTimeout(blurTimeoutId)
      }

      blurTimeoutId = setTimeout(() => {
        if (!formattedValue && !hasValueFromCombobox) {
          setIsFocused(false)
        }

        blurTimeoutId = null
      }, 100)
    }

    function handleFocus(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
      props?.onFocus?.(e)
      setIsFocused(true)
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      props?.onChange?.(e)
    }

    const placeholder = getPlaceholder(type) || props.placeholder || label
    const className = cn(variants({ showError, showWarning, type, applyCobranding }))

    const labelId = useRef(uuidv4())

    return (
      <div className={cn('relative', props.className)}>
        {type === 'textArea' ? (
          <textarea
            {...props}
            id={INPUT_ID}
            ref={ref as ForwardedRef<HTMLTextAreaElement>}
            disabled={disabled}
            required={required}
            value={value}
            placeholder={placeholder}
            className={className}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-labelledby={labelId.current}
          />
        ) : (
          <>
            <input
              {...props}
              id={INPUT_ID}
              ref={ref as ForwardedRef<HTMLInputElement>}
              disabled={disabled}
              required={required}
              value={value}
              type={type}
              placeholder={placeholder}
              className={className}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              aria-labelledby={labelId.current}
            />
          </>
        )}
        <Label
          id={labelId.current}
          label={label}
          labelTag={shouldShowLabelTag() && labelTag}
          showError={showError}
          showWarning={showWarning}
          applyCobranding={applyCobranding}
        />
        <Transition
          show={showError || showWarning || showHelperText}
          as="div"
          enter="transition-all duration-150"
          enterFrom="-translate-y-4 opacity-0"
          enterTo="translate-y-0 opacity-100"
          leave="transition-all duration-150"
          leaveFrom="translate-y-0 opacity-100"
          leaveTo="-translate-y-4 opacity-0"
        >
          {getText({ showError, showWarning, showHelperText, error, warning, helperText })}
        </Transition>
      </div>
    )
  }
)

Input.displayName = 'Input'

function getText({
  showError,
  showWarning,
  showHelperText,
  error,
  warning,
  helperText,
}: {
  showError: boolean
  showWarning: boolean
  showHelperText: boolean
  error?: string
  warning?: string
  helperText?: string
}) {
  if (showError) {
    return <Span className="mt-1 block text-sm text-error-600">{error}</Span>
  }

  if (showWarning) {
    return <Span className="mt-1 block text-sm text-warning-600">{warning}</Span>
  }

  if (showHelperText) {
    return <Span className="mt-1 block text-sm text-moss-700">{helperText}</Span>
  }
}

function getLabelTag({ required, tagType }: { required?: boolean; tagType: TagType | null }) {
  if (required || tagType === 'required') return <RequiredTag />
  if (tagType === 'optional') return <OptionalTag />
  if (tagType === 'recommended') return <RecommendedTag />

  return null
}

function getPlaceholder(type: InputType): string {
  return match(type)
    .with('text', 'textArea', () => '')
    .with('dateString', () => 'mm/dd/yyyy')
    .with('currency', () => 'e.g. $75,000')
    .with('email', () => 'e.g. janedoe@gmail.com')
    .with('tel', () => 'e.g. 1 (555) 555 - 5555')
    .with('password', () => '')
    .exhaustive()
}

function formatValue({ type, value }: { type: InputType; value?: string | number }) {
  if (typeof value !== 'string') return value

  return match(type)
    .with('text', 'email', 'textArea', () => value)
    .with('dateString', () => formatDateWithSlash(value))
    .with('tel', () => formatPhoneNumber(value))
    .with('currency', () => formatCurrency(value))
    .with('password', () => value)
    .exhaustive()
}
